import React, { useState } from 'react';
import styles from './CreditReport.module.scss';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import {
    headers,
    HEADER_IDS,
    PDF_HEADERS,
    UNRATED_MESSAGE,
    EMPTY_CELL,
    VIEW_LABEL,
    VIEW_DETAILS_LABEL,
    UPDATED_ON_LABEL,
    SHORT_TERM_RATING_LABEL,
    LONG_TERM_RATING_LABEL,
    OUTLOOK_LABEL,
    NOT_AVAILABLE_LABEL,
} from './CreditReportConstants';
import { formatDateByFormat, DATE_FORMATS } from '../../../../../../utils/dateUtils';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import OutlookPill from './components/OutlookPill/OutlookPill';
import CreditRatingAgencyDrawer from '../CreditRatingAgencyDrawer/CreditRatingAgencyDrawer';
import type { ProbeCreditRatingSummaryRow, ProbeCreditRatingView } from './creditReport.types';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';

interface CreditReportProps {
    isPDFReport?: boolean;
    probeCreditRating?: ProbeCreditRatingView | null;
    companyName?: string;
    isLoading?: boolean;
}

function CreditReport({ isPDFReport = false, probeCreditRating, companyName = '', isLoading = false }: CreditReportProps) {
    const summary = probeCreditRating?.summary ?? [];
    const isRatingUnavailable = probeCreditRating === null;
    const isUnrated = summary.length === 0;
    const emptyMessage = isRatingUnavailable ? NOT_AVAILABLE_LABEL : UNRATED_MESSAGE;
    const isMobile = useIsMobile();
    const [openAgencyKey, setOpenAgencyKey] = useState<string | null>(null);
    const [ratingForOpenAgency, setRatingForOpenAgency] = useState(probeCreditRating);
    if (ratingForOpenAgency !== probeCreditRating) {
        setRatingForOpenAgency(probeCreditRating);
        setOpenAgencyKey(null);
    }
    const openAgency = probeCreditRating?.agencies?.find((agency) => agency.agency === openAgencyKey);
    const tableHeaders = isPDFReport ? PDF_HEADERS : headers;

    const getRowData = (headerId: string, item: ProbeCreditRatingSummaryRow) => {
        switch (headerId) {
            case HEADER_IDS.DATE: {
                const cardDate = formatDateByFormat(item.date, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);
                const yearSeparatorIndex = cardDate.lastIndexOf(' ');
                return (
                    <div className={clsx(styles.rowItem, styles.dateCell)}>
                        {cardDate ? (
                            <>
                                <span className={styles.dateDayMonth}>{cardDate.slice(0, yearSeparatorIndex)}</span>{' '}
                                {cardDate.slice(yearSeparatorIndex + 1)}
                            </>
                        ) : (
                            EMPTY_CELL
                        )}
                    </div>
                );
            }
            case HEADER_IDS.AGENCY:
                return <div className={clsx(styles.rowItem, styles.agencyCell)}>{item.agencyDisplay}</div>;
            case HEADER_IDS.OUTLOOK:
                return (
                    <div className={clsx(styles.rowItem, styles.outlookCell)}>
                        <OutlookPill outlook={item.outlook} />
                    </div>
                );
            case HEADER_IDS.DETAILS:
                return (
                    <div className={clsx(styles.rowItem, styles.detailsCell)}>
                        <button type="button" className={styles.viewButton}>
                            <TopRightArrowIcon className={styles.viewIcon} />
                            {VIEW_LABEL}
                        </button>
                    </div>
                );
            default:
                return <div className={styles.rowItem}>{item[headerId as 'shortTerm' | 'longTerm'] || EMPTY_CELL}</div>;
        }
    };

    const renderMobileCards = () => (
        <div className={styles.mobileCards}>
            {summary.map((item) => (
                <div className={styles.mobileCard} key={item.agency}>
                    <div className={styles.mCardHeader}>
                        <div className={styles.mAgency}>
                            <span className={styles.mAgencyName}>{item.agencyDisplay}</span>
                            <span className={styles.mUpdatedOn}>
                                {UPDATED_ON_LABEL} {formatDateByFormat(item.date, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) || EMPTY_CELL}
                            </span>
                        </div>
                    </div>
                    <div className={styles.mDivider} />
                    <div className={styles.mRows}>
                        <div className={styles.mRow}>
                            <span className={styles.mLabel}>{LONG_TERM_RATING_LABEL}</span>
                            <span className={styles.mValue}>{item.longTerm || EMPTY_CELL}</span>
                        </div>
                        <div className={styles.mRow}>
                            <span className={styles.mLabel}>{SHORT_TERM_RATING_LABEL}</span>
                            <span className={styles.mValue}>{item.shortTerm || EMPTY_CELL}</span>
                        </div>
                        <div className={styles.mRow}>
                            <span className={styles.mLabel}>{OUTLOOK_LABEL}</span>
                            <span className={styles.mValue}>
                                <OutlookPill outlook={item.outlook} />
                            </span>
                        </div>
                    </div>
                    <div className={styles.mDivider} />
                    <button type="button" className={styles.mViewDetails} onClick={() => setOpenAgencyKey(item.agency)}>
                        <TopRightArrowIcon className={styles.mViewIcon} />
                        {VIEW_DETAILS_LABEL}
                    </button>
                </div>
            ))}
        </div>
    );

    const renderTable = () => (
        <div className={clsx(styles.table, { [styles.pdfTable]: isPDFReport })}>
            <div className={styles.tableHeader}>
                {tableHeaders.map((header) => (
                    <div className={styles.headerItem} key={header.id}>
                        {header.label}
                    </div>
                ))}
            </div>
            <div className={styles.tableBody}>
                {summary.map((item) => (
                    <div
                        className={clsx(styles.tableRow, { [styles.clickableRow]: !isPDFReport })}
                        key={item.agency}
                        onClick={isPDFReport ? undefined : () => setOpenAgencyKey(item.agency)}
                    >
                        {tableHeaders.map((header) => (
                            <React.Fragment key={`${item.agency}-${header.id}`}>{getRowData(header.id, item)}</React.Fragment>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );

    const showMobileCards = isMobile && !isPDFReport;
    const isDesktopCard = !isPDFReport && !isMobile;

    const renderContent = () => {
        if (isLoading) {
            return <LoadingSpinner color="var(--primary-text-colour)" height="35px" />;
        }
        if (isRatingUnavailable || isUnrated) {
            return <div className={styles.unratedMessage}>{emptyMessage}</div>;
        }
        return (
            <>
                {!showMobileCards && renderTable()}
                {!isPDFReport && renderMobileCards()}
            </>
        );
    };

    // A PDF export is a snapshot, so a section still loading is left out rather than captured as a spinner.
    if (isPDFReport && isLoading) return null;

    const render = (
        <div className={styles.creditReport}>
            <HeaderBodyBox title={REPORT_SECTION_NAMES.CREDIT_RATING} isPDFReport={isPDFReport}>
                <div className={clsx(styles.content, { [styles.responsiveContent]: isDesktopCard })}>{renderContent()}</div>
            </HeaderBodyBox>
            {openAgency && (
                <CreditRatingAgencyDrawer agency={openAgency} companyName={companyName} onClose={() => setOpenAgencyKey(null)} />
            )}
        </div>
    );

    if (isPDFReport) {
        return <A4Page sectionName={REPORT_SECTION_NAMES.CREDIT_RATING}>{render}</A4Page>;
    } else {
        return render;
    }
}

export default CreditReport;
